'use client'

import { placesBrowserKey, type Place } from '@/lib/google-places'
import { AddressAutocomplete } from '@/components/address-autocomplete'
import { FieldLabel, inputClass } from '@/components/form-field'

/** What a form keeps of a picked address. The postcode goes to the form's own postcode box. */
export type PickedAddress = {
  jobAddress: string
  postcode: string
  placeId: string
  lat: number
  lng: number
}

export const pickedAddress = (place: Place): PickedAddress => ({
  jobAddress: place.formatted.replace(/,\s*Australia$/, ''),
  postcode: place.postcode,
  placeId: place.placeId,
  lat: place.lat,
  lng: place.lng,
})

/**
 * An optional job address box with Australian suggestions, above a form's postcode. Choosing a
 * suggestion fills the postcode and remembers the place. With no browser key it is not there at
 * all, and the form is the form it always was. Typing an address Google does not offer works too:
 * the postcode box is still the way in.
 */
export function JobAddressField({
  id,
  value,
  disabled,
  label = 'Job address (optional)',
  onTyped,
  onPicked,
  apiKey,
}: {
  id: string
  value: string
  disabled?: boolean
  label?: string
  onTyped: (value: string) => void
  onPicked: (picked: PickedAddress) => void
  apiKey?: string
}) {
  if (!(apiKey ?? placesBrowserKey())) return null
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <AddressAutocomplete
        id={id}
        className={inputClass}
        value={value}
        disabled={disabled}
        apiKey={apiKey}
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="next"
        placeholder="Start typing the street address"
        onValueChange={onTyped}
        onPlace={(place) => onPicked(pickedAddress(place))}
      />
    </div>
  )
}
